import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { usePortal } from "../../classes/contexts/PortalContext";
import { ButtonSize } from "../../classes/enums/ButtonEnum";
import IconsEnum from "../../classes/enums/IconsEnum";
import useTranslation from "../../hooks/i18n/useTranslation";
import IconButton from "../iconButton/IconButton";
import { TooltipOrientation } from "../tooltip/Tooltip";
import "./MovablePopup.css";

const MovablePopup = ({
  children,
  title,
  subtitle,
  onClose,
  initialPos = { x: 10, y: 25 },
  initialWidth = 300,
  initialHeight = 400, // Added initial height parameter
}) => {
  const { t } = useTranslation();
  const portalRoot = usePortal();
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [position, setPosition] = useState(initialPos);
  const [size, setSize] = useState({
    width: initialWidth,
    height: initialHeight, // Use the initial height parameter
  });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [prevState, setPrevState] = useState(null); // Store previous state for fullscreen toggle
  const popupRef = useRef(null);

  // This effect now only runs once on component mount to set initial size
  // if no height is specified
  useEffect(() => {
    if (!initialHeight) {
      const verticalPadding = 50;
      const windowHeight = window.innerHeight;
      setSize(prevSize => ({
        ...prevSize,
        height: windowHeight - verticalPadding,
      }));
    }
  }, [initialHeight]);

  useEffect(() => {
    const handleMouseMove = e => {
      if (isDragging) {
        // Calculate the new position based on the mouse coordinates
        let newX = e.clientX - dragOffset.x;
        let newY = e.clientY - dragOffset.y;

        // If the popup element is available, get its dimensions and clamp the values
        if (popupRef.current) {
          const { width, height } = popupRef.current.getBoundingClientRect();

          // Prevent moving off the left or top edge (ensuring the header stays visible)
          if (newX < 0) newX = 0;
          if (newY < 0) newY = 0;

          // Prevent moving off the right edge
          if (newX + width > window.innerWidth) {
            newX = window.innerWidth - width;
          }

          // Prevent moving off the bottom edge
          if (newY + height > window.innerHeight) {
            newY = window.innerHeight - height;
          }
        }

        setPosition({ x: newX, y: newY });
      } else if (isResizing) {
        // Update both width and height during resize
        setSize({
          width: Math.max(200, e.clientX - position.x),
          height: Math.max(150, e.clientY - position.y), // Added height resizing
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, isResizing, dragOffset, position]);

  const startDragging = e => {
    if (isFullscreen) return; // Disable dragging in fullscreen
    e.preventDefault();
    e.stopPropagation();
    const rect = popupRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
    setIsDragging(true);
  };

  const startResizing = e => {
    if (isFullscreen) return; // Disable resizing in fullscreen
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
  };

  const handleClose = e => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };

  const toggleFullscreen = e => {
    e.preventDefault();
    e.stopPropagation();

    if (!isFullscreen) {
      // Save current state before going fullscreen
      setPrevState({
        position: { ...position },
        size: { ...size },
      });

      // Set to fullscreen
      setPosition({ x: 0, y: 0 });
      setSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
      setIsFullscreen(true);
    } else {
      // Restore previous state
      if (prevState) {
        setPosition(prevState.position);
        setSize(prevState.size);
      }
      setIsFullscreen(false);
    }
  };

  const popupContent = (
    <div
      ref={popupRef}
      className={`draggable-window${isFullscreen ? " fullscreen" : ""}`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${size.width}px`,
        height: `${size.height}px`,
      }}
    >
      <div
        className={`window-header${isFullscreen ? " fullscreen" : ""}`}
        onMouseDown={startDragging} // Move event handler to header only
      >
        <div className="header-text">
          <div className="title">{title}</div>
          {subtitle && <div className="subtitle">{subtitle}</div>}
        </div>
        <div className="header-buttons">
          <IconButton
            icon={isFullscreen ? IconsEnum.EXITFULLSCREEN : IconsEnum.EXPAND}
            title={t(isFullscreen ? "456" : "435")}
            onClick={toggleFullscreen}
            size={ButtonSize.SMALL}
            noBgColor={true}
            tooltipOrientation={TooltipOrientation.Bottom}
          />
          <IconButton
            icon={IconsEnum.CLOSE}
            title={t("443")}
            onClick={handleClose}
            size={ButtonSize.SMALL}
            noBgColor={true}
            tooltipOrientation={TooltipOrientation.Bottom}
          />
        </div>
      </div>
      <div className="window-content" onMouseDown={e => e.stopPropagation()}>
        <div className="image-container">{children}</div>
      </div>
      <div
        className={`resize-handle${isFullscreen ? " hidden" : ""}`}
        onMouseDown={startResizing}
      ></div>
    </div>
  );

  if (!portalRoot) return null;

  return ReactDOM.createPortal(popupContent, portalRoot);
};

export default MovablePopup;
